import type { StepDefinition } from "./workflowParser";
import type { ConditionNodeData } from "../nodes/ConditionNode";
import type { ActionNodeData } from "../nodes/ActionNode";

export interface ValidationError {
    stepId: string;
    stepTitle: string;
    message: string;
}

/**
 * Validates the workflow steps before serialization.
 * Returns an empty array if everything is valid.
 */
export function validateWorkflow(steps: StepDefinition[], workflowName: string): ValidationError[] {
    const errors: ValidationError[] = [];

    if (!workflowName.trim()) {
        errors.push({ stepId: "", stepTitle: "Workflow", message: "Workflow name is required." });
    }

    const configuredSteps = steps.filter((s) => s.type !== "trigger");

    if (configuredSteps.length === 0) {
        errors.push({ stepId: "", stepTitle: "Workflow", message: "Add at least one condition or action." });
        return errors;
    }

    for (const step of configuredSteps) {
        if (step.data?.isPlaceholder) {
            errors.push({
                stepId: step.id,
                stepTitle: step.type === "condition" ? "Condition" : "Action",
                message: `Unconfigured ${step.type} step — select a type or remove it.`,
            });
            continue;
        }

        if (step.type === "condition") {
            const condErrors = validateConditionStep(step);
            errors.push(...condErrors);
        } else if (step.type === "action") {
            const actErrors = validateActionStep(step);
            errors.push(...actErrors);
        }
    }

    return errors;
}

function validateConditionStep(step: StepDefinition): ValidationError[] {
    const data = step.data as ConditionNodeData;
    const title = data.title ?? "Condition";

    switch (data.conditionCode) {
        case "Condition.Message.KeywordMatch":
            if (!data.keywords?.length) {
                return [{ stepId: step.id, stepTitle: title, message: "Add at least one keyword." }];
            }
            break;
        case "Condition.Message.NumberMatch":
            if (!data.phoneNumbers?.length) {
                return [{ stepId: step.id, stepTitle: title, message: "Select at least one phone number." }];
            }
            break;
        case "Condition.Location.Countries":
            if (!data.countries?.length) {
                return [{ stepId: step.id, stepTitle: title, message: "Select at least one country." }];
            }
            break;
    }

    return [];
}

function validateActionStep(step: StepDefinition): ValidationError[] {
    const data = step.data as ActionNodeData;
    const title = data.title ?? "Action";

    if (!data.actionCode && !data.actionType) {
        return [{ stepId: step.id, stepTitle: title, message: "Select an action type." }];
    }

    switch (data.actionCode) {
        case "Action.Message.AutomatedResponse":
            if (!data.templateId && !data.message?.trim() && !data.hasMedia) {
                return [{ stepId: step.id, stepTitle: title, message: "Enter a response message or select a template." }];
            }
            break;
        case "Action.Notify.NotifyUser":
            const hasRecipient =
                data.recordOwner ||
                data.accountOwner ||
                (data.includeAdditionalEmails && (data.additionalEmails?.length ?? 0) > 0);
            if (!hasRecipient) {
                return [
                    {
                        stepId: step.id,
                        stepTitle: title,
                        message: "Select at least one email recipient (Record Owner, Account Owner, or Additional Email Addresses).",
                    },
                ];
            }
            if (data.includeAdditionalEmails && (!data.additionalEmails || data.additionalEmails.length === 0)) {
                return [
                    {
                        stepId: step.id,
                        stepTitle: title,
                        message: "Add at least one email address when Additional Email Addresses is selected.",
                    },
                ];
            }
            break;
    }

    return [];
}
